<script lang="ts">
	import type { StatusOption, SearchField, Status, SearchFieldProps } from 'utility/search-fields'
	import type { SvelteSet } from 'svelte/reactivity'
	import type { ComponentProps } from 'svelte'
	import type { i18n } from 'types/common'

	import Select from '@isoftdata/svelte-select'
	import EditModeControls from './EditModeControls.svelte'
	import Pin from './Pin.svelte'
	import { getEventValue } from '@isoftdata/browser-event'
	import { getContext } from 'svelte'

	interface Props
		extends
			Omit<ComponentProps<typeof Select<Status | null>>, 'options' | 'value' | 'emptyText'>,
			SearchFieldProps<Status | null> {
		status?: Status | '@' | null
	}

	let {
		searchField,
		inEditMode,
		pinnedSearchFields = $bindable(),
		selectedSearchFieldsKeys = $bindable(),
		status = $bindable('@'),
		disabled = false,
		change: changeCb,
		...rest
	}: Props = $props()

	const { t: translate } = getContext<i18n>('i18next')

	let statusList: Array<StatusOption> = [
		{ abbrev: '@', description: translate('search.statusAOrH', 'Status A or H') },
		{ abbrev: 'A', description: translate('common:statusavailable', 'Available') },
		{ abbrev: 'H', description: translate('common:statusonHold', 'On Hold') },
		{ abbrev: 'S', description: translate('common:statussold', 'Sold') },
		{ abbrev: 'C', description: translate('common:statuscrushed', 'Crushed') },
		{ abbrev: 'D', description: translate('common:statusdeleted', 'Deleted') },
		{ abbrev: 'T', description: translate('common:statustransferred', 'Transferred') },
		{ abbrev: 'B', description: translate('common:statusbidding', 'Bidding') },
	]

	function isStatusAbbreviation(string: string): string is StatusOption['abbrev'] {
		return !!statusList.find(status => status.abbrev === string)
	}

	function change(e: Event) {
		const status = getEventValue(e)
		if (isStatusAbbreviation(status)) {
			changeCb?.({ field: searchField, value: status })
		}
	}
</script>

<EditModeControls
	{searchField}
	{inEditMode}
	{pinnedSearchFields}
	{selectedSearchFieldsKeys}
>
	<Select
		{...rest}
		label={translate('common:status', 'Status')}
		emptyText={translate('common:anyStatus', 'Any Status')}
		disabled={disabled || inEditMode}
		bind:value={status}
		onchange={change}
	>
		{#snippet hint()}
			<Pin isPinned={pinnedSearchFields.has(searchField.key)} />
		{/snippet}

		{#each statusList as status}
			<option value={status?.abbrev ?? null}>{status?.abbrev} - {status?.description}</option>
		{/each}
	</Select>
</EditModeControls>
